import {transition, trigger, useAnimation} from '@angular/animations';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {PageEvent} from '@angular/material/paginator';
import {Router} from '@angular/router';
import {PageSizeTables} from '@app/models/enums/page-size-tables.enum';
import {GenericSearchTableActionsDirective} from '@app/service-provider/util/generic-search-table-actions.directive';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {GtPageSizeService} from '@app/services/gt-page-size.service';
import {fadeAnimation} from '@app/shared/animations/fade.animation';
import {BuilderFactory} from '@app/shared/utils/builder.factory';
import {
  DirectoryService,
  GetServiceProvidersResponse,
  GetServicesResponse,
  NameValueService,
  Organization,
  SearchOrganizationResponse,
  ServiceAdministrationService
} from '@gt-api';
import {TranslateService} from '@ngx-translate/core';
import {OrganizationDialogData, OrganizationEditorComponent} from '../organization-editor/organization-editor.component';
import {OrganizationTableEntry} from './organization-table-entry';
import {ConfirmDeleteDialogComponent} from '@app/shared/confirm-delete-dialog/confirm-delete-dialog.component';
import {checkResponse} from '@app/shared/utils/app-utils';
import {catchError, tap} from 'rxjs/operators';
import {EMPTY} from 'rxjs';

@Component({
  templateUrl: './organization-search.component.html',
  styleUrls: ['./organization-search.component.scss', '../util/generic-search-table-actions.directive.scss'],
  animations: [trigger('fadeIn', [transition('* => shown', [useAnimation(fadeAnimation)])])]
})
export class OrganizationSearchComponent
  extends GenericSearchTableActionsDirective<OrganizationTableEntry, Organization>
  implements OnInit {
  dataSourceColumns = [];
  providers: string[];
  inboundServices: string[];
  outboundServices: string[];
  pageSize: number;

  private readonly defaultPageSize = 25;

  constructor(
    public dialog: MatDialog,
    private nameValueService: NameValueService,
    private authenticationService: GtAuthenticationService,
    private directoryService: DirectoryService,
    private router: Router,
    private serviceAdministrationService: ServiceAdministrationService,
    private notificationService: GtNotificationService,
    private translateService: TranslateService,
    private pageSizeService: GtPageSizeService
  ) {
    super();
  }

  ngOnInit() {
    this.dataSourceColumns = this.authenticationService.organizationAccessRights.edit
      ? ['id', 'name', 'provider', 'euInService', 'euOutService', 'actions']
      : ['id', 'name', 'actions'];
    this.pageSize = this.pageSizeService.getPageSize(PageSizeTables.OrganizationSearchTable, this.defaultPageSize);

    this.serviceAdministrationService
      .getInboundServices1(this.authenticationService.oname, this.authenticationService.login)
      .subscribe((response: GetServicesResponse) => {
        this.inboundServices = response.services;
      });
    this.serviceAdministrationService
      .getOutboundServices1(this.authenticationService.oname, this.authenticationService.login)
      .subscribe((response: GetServicesResponse) => {
        this.outboundServices = response.services;
      });
    this.directoryService.getServiceProviders1(this.authenticationService.oname).subscribe((response: GetServiceProvidersResponse) => {
      this.providers = response.providers;
    });

    if (this.sortForDataSource) {
      this.dataSource.sortingDataAccessor = (data: OrganizationTableEntry, property: string) => {
        switch (property) {
          case 'id':
            return data.id ? data.id.toLocaleLowerCase() : '';
          case 'name':
            return data.name ? data.name.toLocaleLowerCase() : '';
          case 'provider':
            return data.provider ? data.provider.toLocaleLowerCase() : '';
          case 'euInService':
            return data.euInService ? data.euInService.toLocaleLowerCase() : '';
          case 'euOutService':
            return data.euInService ? data.euOutService.toLocaleLowerCase() : '';
          default:
            return '';
        }
      };
    }
  }

  showRow(entry: OrganizationTableEntry): void {
    this.router.navigate(['/admin'], {queryParams: {contactOname: entry.entry.id}});
  }

  addRow() {
    if (this.hasAddOrganizationRights()) {
      const dialogData: OrganizationDialogData = {
        organization: {},
        providers: this.providers,
        inboundServices: this.inboundServices,
        outboundServices: this.outboundServices,
        editMode: false
      };

      this.openDialog(dialogData);
    }
  }

  editRow(entry: OrganizationTableEntry) {
    if (this.hasEditOrganizationRights()) {
      const dialogData: OrganizationDialogData = {
        organization: {...entry.entry},
        providers: this.providers,
        inboundServices: this.inboundServices,
        outboundServices: this.outboundServices,
        editMode: true
      };

      this.openDialog(dialogData);
    }
  }

  saveRow(entry: OrganizationTableEntry) {}

  openDialog(dialogData: OrganizationDialogData) {
    const dialogRef = this.dialog.open(OrganizationEditorComponent, {
      minWidth: 500,
      disableClose: true,
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(updateSearch => {
      if (updateSearch) {
        this.search(dialogRef.componentInstance.organization.id);
      }
    });
  }

  newTableEntry(entry: Organization, editing: boolean): OrganizationTableEntry {
    return (
      BuilderFactory.createBuilder<OrganizationTableEntry>()
        .editing(editing)
        .id(entry.id)
        .name(entry.name)
        .entry(entry)
        .euInService(entry.euInService)
        .euOutService(entry.euOutService)
        .provider(entry.provider)
        // .identificationNumber(entry.depNameValues.IDNo || '');
        .build()
    );
  }

  search(searchString: string) {
    this.startLoading();
    this.directoryService.searchOrganization1(this.authenticationService.oname, searchString, searchString, undefined, undefined).subscribe(
      (response: SearchOrganizationResponse) => {
        if (this.stopLoading()) {
          const data = response.code === 200 ? response.organizationList : [];
          this.dataSource.data = data.map(e => this.newTableEntry(e, false));
        }
      },
      () => this.stopLoading()
    );
  }

  hasAddOrganizationRights(): boolean {
    return this.authenticationService.organizationAccessRights.add;
  }

  hasEditOrganizationRights(): boolean {
    return this.authenticationService.organizationAccessRights.edit;
  }

  hasDeleteOrganizationRights(): boolean {
    return this.authenticationService.organizationAccessRights.delete;
  }

  openDeleteDialog(entry: OrganizationTableEntry) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      autoFocus: false,
      data: entry.name
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteOrganization(entry);
      }
    });
  }

  deleteOrganization(entry: OrganizationTableEntry) {
    this.directoryService
      .deleteOrganization1(entry.entry.id)
      .pipe(
        checkResponse(),
        tap(() => {
          this.search(this.filter.nativeElement.value);
          this.notificationService.showSuccessMessage();
        }),
        catchError(error => {
          this.notificationService.showErrorCustomMessage(this.translateService.instant('delete_org.error'));
          return EMPTY;
        })
      )
      .subscribe();
  }

  paging(pageEvent: PageEvent) {
    this.pageSizeService.setPageSize(PageSizeTables.OrganizationSearchTable, pageEvent.pageSize);
  }
}
